import { fg } from '@atlassian/jira-feature-gating';
import { isSystemFieldType, isDeliveryFieldType } from './isSystemFieldType.tsx';
import { FIELD_TYPES } from './fieldTypes.tsx';
import type { FieldType } from './FieldType.types.tsx';
import { isGlobalSystemFieldStableId } from './isGlobalSystemFieldStableId.tsx';
import type { StableId } from './StableId.types.tsx';
import type { ConnectionFieldIssueTypeFilter } from './ConnectionFieldIssueTypeFilter.types.tsx';
import type { Field, FieldKey } from './FieldKey.types.tsx';
import { isIntervalFieldWithDeliveryDateConfig } from './isIntervalFieldWithDeliveryDateConfig.tsx';

export const toFieldKey = (key: string): FieldKey => key;

export const getFieldId = (fieldKey: string): string => {
	const customFieldPrefix = 'customfield_';

	if (!fieldKey || !fieldKey.startsWith(customFieldPrefix)) {
		return '';
	}

	return fieldKey.replace(customFieldPrefix, '');
};

const FIELDS_CANNOT_BE_HIDDEN: Set<FieldType> = new Set([
	FIELD_TYPES.SUMMARY,
	FIELD_TYPES.ISSUE_KEY,
	FIELD_TYPES.CREATED,
]);

/** We only allow hiding jpd system fields, and even then there are a few exceptions. */
export const fieldCanBeHidden = (fieldType?: FieldType): boolean => {
	if (!fieldType || !isSystemFieldType(fieldType)) {
		return false;
	}

	return !FIELDS_CANNOT_BE_HIDDEN.has(fieldType);
};

const FIELDS_CANNOT_BE_MAPPED_TO_GLOBAL: Set<FieldType> = new Set([
	FIELD_TYPES.FORMULA,
	FIELD_TYPES.REACTIONS,
	FIELD_TYPES.VOTES,
	FIELD_TYPES.CONNECTION,
]);

export const canHaveValuesCopiedToGlobalField = (fieldType?: FieldType): boolean | undefined => {
	return (
		fieldType && !isSystemFieldType(fieldType) && !FIELDS_CANNOT_BE_MAPPED_TO_GLOBAL.has(fieldType)
	);
};

/** Predicate for filtering global fields to determine which are eligible to copy values to */
export const eligibleCopyTargetFilterPredicate = ({
	from: sourceFieldType,
	to: targetFieldType,
}: {
	from: FieldType | undefined;
	to: FieldType;
}): boolean => {
	if (targetFieldType === sourceFieldType) {
		return true;
	}

	return (
		(sourceFieldType === FIELD_TYPES.MULTI_SELECT &&
			targetFieldType === FIELD_TYPES.JSW_MULTI_SELECT) ||
		(sourceFieldType === FIELD_TYPES.PEOPLE && targetFieldType === FIELD_TYPES.JSW_PEOPLE)
	);
};

const SYSTEM_FIELDS: Set<FieldType> = new Set([
	FIELD_TYPES.ISSUE_COMMENTS,
	FIELD_TYPES.INSIGHTS,
	FIELD_TYPES.LINKED_ISSUES,
	FIELD_TYPES.DELIVERY_PROGRESS,
	FIELD_TYPES.DELIVERY_STATUS,
	FIELD_TYPES.ARCHIVED,
	FIELD_TYPES.ARCHIVED_ON,
	FIELD_TYPES.ARCHIVED_BY,
	FIELD_TYPES.ATLAS_GOAL,
	FIELD_TYPES.ATLAS_PROJECT,
	FIELD_TYPES.ATLAS_PROJECT_STATUS,
	FIELD_TYPES.PLATFORM_GOALS,
	FIELD_TYPES.PLATFORM_FOCUS_AREAS,
	FIELD_TYPES.INSIGHTS_GLOBAL,
	FIELD_TYPES.LINKED_ISSUES_GLOBAL,
	FIELD_TYPES.DELIVERY_PROGRESS_GLOBAL,
	FIELD_TYPES.DELIVERY_STATUS_GLOBAL,
	FIELD_TYPES.ISSUE_COMMENTS_GLOBAL,
	FIELD_TYPES.PLATFORM_PROJECT,
	FIELD_TYPES.PLATFORM_PROJECT_STATUS,
	FIELD_TYPES.PLATFORM_ATLAS_PROJECT,
]);

const SYSTEM_FIELDS_WITH_PROJECT_CONFIGURATION: Set<FieldType> = new Set([
	FIELD_TYPES.LINKED_ISSUES,
	FIELD_TYPES.DELIVERY_PROGRESS,
	FIELD_TYPES.DELIVERY_STATUS,
	FIELD_TYPES.LINKED_ISSUES_GLOBAL,
	FIELD_TYPES.DELIVERY_PROGRESS_GLOBAL,
	FIELD_TYPES.DELIVERY_STATUS_GLOBAL,
]);

export const isGlobalSystem = (
	type: FieldType,
	global?: boolean,
	custom?: boolean,
	stableId?: StableId,
): boolean => {
	if (type === FIELD_TYPES.PLATFORM_FOCUS_AREAS && !fg('jpd_focus_areas_field_support')) {
		return false;
	}

	return !!global && !!custom && (SYSTEM_FIELDS.has(type) || isGlobalSystemFieldStableId(stableId));
};

export const isGlobalNonSystem = (
	type: FieldType,
	global?: boolean,
	custom?: boolean,
	stableId?: StableId,
): boolean => {
	return (
		!!global && !!custom && !(SYSTEM_FIELDS.has(type) || isGlobalSystemFieldStableId(stableId))
	);
};

export const isGlobalSystemField = ({ global, custom, type, stableId }: Field): boolean => {
	return isGlobalSystem(type, global, custom, stableId);
};

export const isGlobalNonSystemField = ({ global, custom, type, stableId }: Field): boolean => {
	return isGlobalNonSystem(type, global, custom, stableId);
};

export const isGlobalSystemWithProjectLevelConfiguration = (
	type: FieldType,
	global?: boolean,
	custom?: boolean,
	stableId?: StableId,
): boolean => {
	return (
		isGlobalSystem(type, global, custom, stableId) &&
		SYSTEM_FIELDS_WITH_PROJECT_CONFIGURATION.has(type)
	);
};

export const isGlobalSystemFieldWithProjectLevelConfiguration = (field: Field): boolean => {
	return isGlobalSystemWithProjectLevelConfiguration(
		field.type,
		field.global,
		field.custom,
		field.stableId,
	);
};

export const isConnectionFieldConfigured = (
	field: Field,
): field is Field & { configuration: { issueTypeFilters: ConnectionFieldIssueTypeFilter[] } } => {
	return (
		field.type === FIELD_TYPES.CONNECTION &&
		field.configuration?.issueTypeFilters !== undefined &&
		field.configuration?.issueTypeFilters.length > 0
	);
};

export const isFieldDependingOnDeliveryData = (field: Field): boolean =>
	isDeliveryFieldType(field.type) || isIntervalFieldWithDeliveryDateConfig(field);
